// Item drops (A3, monorail-jg9 — plan amoeba-environment-and-drops.md).
// Equipment enters the world as rarity-tinted orbs: predators roll a low,
// depth-scaled chance at their kill choke point (damage.ts), bosses always
// drop with a rarity floor. The drop moment is staged (ripple + pigment
// burst scaled by rarity); pickup adds to inventory immediately — gear is
// never lost — then main.ts opens the compare card via the pickup callback.

import type { Entity, ItemInstance, Rarity } from '../components'
import type { GameWorld } from '../world'
import { addItem, rollItem } from '../items'
import { depthFactor } from '../depth'
import { morphologyFrom } from '../creature-recipe'
import { emitRipple, spawnPigmentPuff } from './particles'
import { playSfx } from '../../engine/audio'
import { T } from '../tunables'

// Shared color language with the stats/pickup cards (index.html .rarity-*).
export const RARITY_TINT: Record<Rarity, [number, number, number]> = {
  uncommon: [0.50, 0.85, 0.63],
  rare:     [0.53, 0.68, 0.95],
  pristine: [0.95, 0.82, 0.46],
  unique:   [0.95, 0.55, 0.55],
}

const RARITY_RANK: Record<Rarity, number> = { uncommon: 0, rare: 1, pristine: 2, unique: 3 }

/** Stage the drop: the orb itself plus a rarity-scaled splash. */
export function spawnItemOrb(world: GameWorld, x: number, y: number, item: ItemInstance, claimKey?: string): void {
  const tint = RARITY_TINT[item.rarity]
  world.add({
    position: { x, y },
    velocity: { x: 0, y: 0 },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 1.4, amp: 0.16 },
    renderable: { shape: 'itemOrb', radius: T.orbs.itemOrbRadius, tint },
    // FEAT_HEX like the old mutation orbs (geometric = item); the rarity tint and
    // the pigment ring are what say "equipment".
    morphology: morphologyFrom({ body: 'hex' }, Math.random()),
    itemOrb: claimKey !== undefined ? { item, claimKey } : { item },
  })
  // Rarity-scaled burst: uncommon whispers, pristine makes a scene.
  const burst = 3 + RARITY_RANK[item.rarity] * 3
  for (let i = 0; i < burst; i++) {
    const a = (i / burst) * Math.PI * 2
    const r = 0.08 + Math.random() * 0.08
    spawnPigmentPuff(world, x + Math.cos(a) * r, y + Math.sin(a) * r, tint, 0.04)
  }
  emitRipple(x, y, { strength: 0.3 + RARITY_RANK[item.rarity] * 0.25, lifetime: 0.6 })
}

// Session pity (playtest round 1: the user's first session produced zero
// drops, so the pickup ritual never taught itself). The first predator kill
// of every session drops — the reveal is guaranteed within minutes of play.
// Module lifetime = session; deliberately NOT reset on respawn.
let sessionPityPending = true

/** Test hook. */
export function resetSessionPity(pending = true): void {
  sessionPityPending = pending
}

/** Predator kill roll — guaranteed on the session's first kill, then a low
 * chance growing with depth. Exported with an injectable rng so the roll is
 * testable; damage.ts calls it with the default. */
export function maybeDropItemForPredator(
  world: GameWorld,
  pred: Entity,
  rng: () => number = Math.random,
): boolean {
  const c = T.items.drop
  const chance = c.predatorBase + c.predatorDepthBonus * depthFactor(pred)
  if (!sessionPityPending && rng() >= chance) return false
  sessionPityPending = false
  const slot = rng() < 0.5 ? 'hat' : 'weapon'
  spawnItemOrb(world, pred.position.x, pred.position.y, rollItem(slot, { rng }))
  return true
}

/** Boss kills always drop, and never at floor rarity — the reliable jackpot
 * that makes a boss worth the fight. */
export function dropBossItem(
  world: GameWorld,
  x: number,
  y: number,
  rng: () => number = Math.random,
): void {
  const slot = rng() < 0.5 ? 'hat' : 'weapon'
  const item = rollItem(slot, { rng })
  if (item.rarity === 'uncommon') {
    spawnItemOrb(world, x, y, rollItem(slot, { rng, rarity: 'rare', base: item.base }))
    return
  }
  spawnItemOrb(world, x, y, item)
}

/** Escalating collect sound: one chime per rarity rank above floor. */
function playPickupSound(rarity: Rarity): void {
  const chimes = 1 + RARITY_RANK[rarity]
  for (let i = 0; i < chimes; i++) {
    setTimeout(() => playSfx('orb_collect', { volume: 0.5 + i * 0.12 }), i * 110)
  }
}

export function itemOrbSystem(
  world: GameWorld,
  player: Entity,
  dt: number,
  onPickup: (item: ItemInstance, claimKey?: string) => void,
): void {
  const playerR = player.renderable?.radius ?? 0.12
  const magnetRange = player.mods?.orbMagnetRange ?? 0

  for (const orb of [...world.with('itemOrb', 'position', 'renderable')]) {
    // Idle twinkle — a slow sparkle of pigment so an unclaimed drop keeps
    // announcing itself (and reads distinct from mutation orbs), faster at
    // higher rarity.
    const rank = RARITY_RANK[orb.itemOrb!.item.rarity]
    if (Math.random() < dt * (0.9 + rank * 0.7)) {
      const a = Math.random() * Math.PI * 2
      const r = orb.renderable.radius * (0.8 + Math.random() * 0.6)
      spawnPigmentPuff(world, orb.position.x + Math.cos(a) * r, orb.position.y + Math.sin(a) * r,
        RARITY_TINT[orb.itemOrb!.item.rarity], 0.025)
    }

    // Grabby / Static cling magnetize item drops like every other orb.
    if (magnetRange > 0) {
      const dx = player.position.x - orb.position.x
      const dy = player.position.y - orb.position.y
      const d = Math.hypot(dx, dy)
      const totalRange = magnetRange + orb.renderable.radius + playerR
      if (d > 0.0001 && d < totalRange) {
        const pull = 1 - d / totalRange
        const step = T.orbs.dnaMagnetPullSpeed * pull * dt
        orb.position.x += (dx / d) * step
        orb.position.y += (dy / d) * step
      }
    }

    const dx = orb.position.x - player.position.x
    const dy = orb.position.y - player.position.y
    const reach = playerR + orb.renderable.radius
    if (dx * dx + dy * dy < reach * reach) {
      const item = orb.itemOrb!.item
      const claimKey = orb.itemOrb!.claimKey
      addItem(player, item) // into the collection first — gear is never lost
      playPickupSound(item.rarity)
      spawnPigmentPuff(world, orb.position.x, orb.position.y, RARITY_TINT[item.rarity], 0.06)
      world.remove(orb)
      onPickup(item, claimKey)
    }
  }
}
